import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { usageApiService } from '../../services/usageApiService'
import type { RenderDayStats, UsageSummary, LatencyPercentiles } from '../../services/usageApiService'
import { ApiError } from '../../services/BaseApiService'
import { isPlanUpgradeError } from '../esign/esignShared'
import { standingTone, standingUnit, standingValue } from '../../utils/billingStanding'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { fmtNum, shortDay } from '../../utils/analyticsFormat'
import { card, DailyChart, ErrorNote, gridAuto, KpiCard, SectionTitle } from './AnalyticsUi'

/** The usage endpoint clamps to 90 days. */
const MAX_DAYS = 90

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1_048_576).toFixed(1)} MB`
}

function LatencyChart({ data }: { data: RenderDayStats[] }) {
  const vals = data.map(d => d.avgDurationMs).filter((v): v is number => v != null)
  if (vals.length === 0) return <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>No latency data</div>
  const maxMs = Math.max(...vals, 1)
  const chartH = 80
  const labelEvery = Math.max(1, Math.ceil(data.length / 7))
  const gap = data.length > 30 ? '1px' : '2px'
  return (
    <div>
      <div style={{ fontSize: '10px', color: colors.textMuted, marginBottom: 4 }}>
        Avg render time / day (max {Math.round(maxMs)}ms)
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap, height: chartH, borderBottom: `1px solid ${colors.border}` }}>
        {data.map(d => {
          const h = d.avgDurationMs != null ? Math.max(1, Math.round((d.avgDurationMs / maxMs) * chartH)) : 0
          return (
            <div
              key={d.day}
              title={d.avgDurationMs != null ? `${shortDay(d.day)}: ${Math.round(d.avgDurationMs)}ms avg` : shortDay(d.day)}
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: chartH }}
            >
              <div style={{ height: h || 1, background: h ? colors.primary : colors.border, opacity: h ? 0.7 : 1, borderRadius: '2px 2px 0 0' }} />
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap, marginTop: 4 }}>
        {data.map((d, i) => (
          <div key={d.day} style={{ flex: 1, minWidth: 0, fontSize: '9px', color: colors.textMuted, textAlign: 'center', whiteSpace: 'nowrap', opacity: i % labelEvery === 0 ? 1 : 0 }}>
            {shortDay(d.day)}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function PerformancePanel({ days }: { days: number }) {
  const effectiveDays = Math.min(days, MAX_DAYS)
  const [data, setData] = useState<RenderDayStats[]>([])
  const [percentiles, setPercentiles] = useState<LatencyPercentiles | null>(null)
  const [summary, setSummary] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgrade, setUpgrade] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    setLoading(true); setError(''); setUpgrade(false)
    usageApiService.getSummary().then(s => { if (live) setSummary(s) }).catch(() => {})
    usageApiService.getAnalytics(effectiveDays)
      .then(a => { if (live) { setData(a.rows); setPercentiles(a.percentiles) } })
      .catch(err => {
        if (!live) return
        if (isPlanUpgradeError(err)) setUpgrade(true)
        else setError(err instanceof ApiError ? err.message : 'Failed to load performance data')
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [effectiveDays])

  const totals = data.reduce(
    (acc, d) => ({ total: acc.total + d.totalRenders, failed: acc.failed + d.failedRenders }),
    { total: 0, failed: 0 },
  )
  const errorRate = totals.total > 0 ? (totals.failed / totals.total) * 100 : 0
  const vals = data.flatMap(d => (d.avgDurationMs != null ? [d.avgDurationMs] : []))
  const avgMs = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null
  const ms = (v: number | null | undefined) => (v != null ? `${fmtNum(v)}ms` : '—')
  const tone = summary ? standingTone(summary.standing) : null

  return (
    <div>
      {summary && (
        <div style={{ ...gridAuto(150), marginBottom: spacing.lg }}>
          {/* Billing is team-wide by design (see UsageStanding), unlike the workspace-scoped charts below. */}
          <KpiCard
            label={`Billable ${standingUnit(summary.standing).toLowerCase()} (month)`}
            value={standingValue(summary.standing, summary.totalRenders)}
            tone={tone === 'blocked' ? colors.error : tone === 'warning' ? colors.warning : undefined}
          />
          <KpiCard label="Pages (month)" value={fmtNum(summary.totalPages)} />
          <KpiCard label="Output (month)" value={fmtBytes(summary.totalBytes)} />
        </div>
      )}

      {upgrade ? (
        <div data-testid="performance-upgrade" style={{
          ...card, background: colors.infoLight, border: `1px solid ${colors.infoBorder}`, color: colors.info, borderRadius: radius.md,
        }}>
          <div style={{ fontWeight: 600, fontSize: fontSize.base, marginBottom: spacing.xs }}>Render performance is an Enhanced plan feature</div>
          <div style={{ fontSize: fontSize.sm }}>
            Upgrade to see render latency percentiles and daily success/failure volume. Document analytics on the other tabs are included on every plan.{' '}
            <Link to="/app/billing" style={{ color: colors.info, fontWeight: 600 }}>View plans</Link>
          </div>
        </div>
      ) : (
        <>
          {error && <ErrorNote message={error} />}
          {days > MAX_DAYS && (
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginBottom: spacing.sm }}>Performance data covers the last {MAX_DAYS} days.</div>
          )}
          <div style={{ ...gridAuto(140), marginBottom: spacing.lg }}>
            <KpiCard label="Renders" value={loading ? '…' : fmtNum(totals.total)} />
            <KpiCard label="Error rate" value={loading ? '…' : `${errorRate.toFixed(1)}%`} tone={errorRate > 5 ? colors.error : undefined} />
            <KpiCard label="Avg latency" value={loading ? '…' : ms(avgMs)} />
            <KpiCard label="P50 latency" value={loading ? '…' : ms(percentiles && Math.round(percentiles.p50DurationMs))} />
            <KpiCard label="P95 latency" value={loading ? '…' : ms(percentiles && Math.round(percentiles.p95DurationMs))}
              tone={percentiles && percentiles.p95DurationMs > 5000 ? colors.warning : undefined} />
            <KpiCard label="P99 latency" value={loading ? '…' : ms(percentiles && Math.round(percentiles.p99DurationMs))}
              tone={percentiles && percentiles.p99DurationMs > 10000 ? colors.error : undefined} />
          </div>
          <div style={{ ...card, marginBottom: spacing.lg }}>
            <SectionTitle>Render volume</SectionTitle>
            {loading ? <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading…</div>
              : data.length > 0 ? (
                <DailyChart
                  rows={data.map(d => ({ day: d.day, ok: d.successRenders, failed: d.failedRenders }))}
                  series={[{ key: 'ok', label: 'Success', color: colors.success }, { key: 'failed', label: 'Failed', color: colors.error }]}
                />
              ) : <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>No data for this period</div>}
          </div>
          <div style={card}>
            <SectionTitle>Render latency</SectionTitle>
            {loading ? <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading…</div> : <LatencyChart data={data} />}
          </div>
        </>
      )}
    </div>
  )
}
